import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cms from '@moka-fe/cms';
import classNames from 'classnames';
import {
  AutoEllipsis,
  Checkbox,
  Dropdown,
  Filter,
  Icon,
  Input,
  Menu,
  Modal,
  Spacing,
  TagSelect,
} from 'sugar-design';

import ApplicationsTable from './_lib/ApplicationsTable';
import CandidateComment from './_lib/CandidateComment';
import JobFilter from './_lib/JobFilter';

import {
  ADDITIONAL_STAGES,
  ADDITIONAL_STAGE_OPTIONS,
  HEADHUNTER_APPLICATION_STATUS,
} from '@/constants/common';
import { fetchApplicationsCountOfRecommendStatus } from '@/redux/actions/applications';
import { createComment, getComment } from '@/redux/actions/comments';
import { getI18n } from '@/utils/i18n';

import styles from './index.cm.styl';

const i18n = getI18n();

// value 不要修改为 boolean 类型，因为 loadApplications 有一步去除 false 值的处理
const HEADHUNTER_TYPE_OPTIONS = [
  { label: i18n.t`猎头`, value: 'false' },
  { label: 'RPO', value: 'true' },
];

const RECOMMEND_STATUS = {
  CANDIDATE_UN_CONFIRM: 'CANDIDATE_UN_CONFIRM', // 候选人未确认
  HR_UN_HANDLE: 'HR_UN_HANDLE', // hr未处理
  RECOMMEND_SUCCESS: 'RECOMMEND_SUCCESS', // 推荐成功
  HR_REJECT: 'HR_REJECT', //  hr拒绝
  CANDIDATE_REJECT: 'CANDIDATE_REJECT', // 候选人拒绝
  CHECK_DUP_ING: 'CHECK_DUP_ING', // 查重进行中
  CHECK_DUP_FAILE: 'CHECK_DUP_FAILE', // 查重失败
};

const RECOMMEND_STATUS_TAG = {
  PENDING: 'PENDING',
  SUCCESS: RECOMMEND_STATUS.RECOMMEND_SUCCESS,
  FAILURE: 'FAILURE',
};

const TAG_OPTIONS = [
  { value: RECOMMEND_STATUS_TAG.PENDING, label: i18n.t`推荐中` },
  { value: RECOMMEND_STATUS_TAG.SUCCESS, label: i18n.t`推荐成功` },
  { value: RECOMMEND_STATUS_TAG.FAILURE, label: i18n.t`推荐失败` },
];

const STATUS_PENDING_OPTIONS = [
  { value: RECOMMEND_STATUS.CANDIDATE_UN_CONFIRM, label: i18n.t`候选人待确认` },
  { value: RECOMMEND_STATUS.HR_UN_HANDLE, label: i18n.t`HR待处理` },
  { value: RECOMMEND_STATUS.CHECK_DUP_ING, label: i18n.t`查重进行中` },
];

const STATUS_REFUSE_OPTIONS = [
  { value: RECOMMEND_STATUS.CANDIDATE_REJECT, label: i18n.t`候选人拒绝` },
  { value: RECOMMEND_STATUS.HR_REJECT, label: i18n.t`HR拒绝` },
  { value: RECOMMEND_STATUS.CHECK_DUP_FAILE, label: i18n.t`查重未通过` },
];

class ApplicationList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showCommentModal: false,
      showRejectedApplication: true,
      showDuplicatedApplication: true,
      statusFilterValue: null,
      jobFilterId: null,
      candidateNameFilterValue: '',
      rpoRecommend: null,
      recommendStatus: RECOMMEND_STATUS.HR_UN_HANDLE,
      recommendTag: RECOMMEND_STATUS_TAG.PENDING,
      page: 1,
      recommendStatusCounts: {
        [RECOMMEND_STATUS_TAG.PENDING]: 0,
        [RECOMMEND_STATUS_TAG.SUCCESS]: 0,
        [RECOMMEND_STATUS_TAG.FAILURE]: 0,
      },
    };
  }

  componentDidMount() {
    if (this.props.jobFilterId) {
      // 如果有 jobFilterId， 说明是从职位列表穿透而来
      this.setState({ jobFilterId: this.props.jobFilterId });
    } else {
      const oldFilter = this.props.filter;
      const filter = {
        // 如果外部数据中没有开关的值，就用内部的状态代替，否则后面会被无数据覆盖掉
        showRejectedApplication:
          oldFilter.showRejectedApplication || this.state.showRejectedApplication,
        statusFilterValue: oldFilter.statusFilterValue,
        jobFilterId: oldFilter.jobFilterId,
        rpoRecommend: oldFilter.rpoRecommend,
      };
      this.setState(filter);
    }

    fetchApplicationsCountOfRecommendStatus(this.props.match.params.orgId).then((counts) =>
      this.setState({
        recommendStatusCounts: {
          [RECOMMEND_STATUS_TAG.PENDING]: counts.recommending ?? 0,
          [RECOMMEND_STATUS_TAG.SUCCESS]: counts.recommendSuccess ?? 0,
          [RECOMMEND_STATUS_TAG.FAILURE]: counts.recommendFailed ?? 0,
        },
      })
    );
  }

  togglePassedOption = () => {
    // 是否显示淘汰的候选人
    const showRejectedApplication = !this.state.showRejectedApplication;
    let nextState = { showRejectedApplication, page: 1 };
    if (this.state.statusFilterValue === ADDITIONAL_STAGES.REJECTED && !showRejectedApplication) {
      nextState = { ...nextState, statusFilterValue: '' };
    }
    this.setState(nextState);
  };

  onStatusFilterChange = (value) => this.setState({ statusFilterValue: value, page: 1 });

  onHeadhunterTypeFilterChange = (value) => this.setState({ rpoRecommend: value, page: 1 });

  onJobFilterChange = (value) => this.setState({ jobFilterId: value, page: 1 });

  onNameChange = (e) => this.setState({ candidateNameFilterValue: e.target.value, page: 1 });

  onStatusChange = (recommendTag) => {
    if (recommendTag) {
      this.setState({
        recommendTag,
        jobFilterId: null,
        candidateNameFilterValue: null,
        statusFilterValue: null,
        showRejectedApplication: true,
        recommendStatus:
          recommendTag === RECOMMEND_STATUS_TAG.SUCCESS
            ? RECOMMEND_STATUS.RECOMMEND_SUCCESS
            : recommendTag === RECOMMEND_STATUS_TAG.PENDING
            ? RECOMMEND_STATUS.HR_UN_HANDLE
            : RECOMMEND_STATUS.HR_REJECT,

        page: 1,
      });
    }
  };

  onRecommendStatusChange = (recommendStatus) => this.setState({ recommendStatus, page: 1 });

  onOpenConversationModal = (application) => {
    const {
      params: { orgId },
    } = this.props.match;
    this.setState({ showCommentModal: true, application });
    this.props.getComment(orgId, application.id);
  };

  closeModal = () => this.setState({ showCommentModal: false });

  render() {
    const {
      relatedJobEntities,
      applyIdToComment = {},
      orgEntities,
      match: {
        params: { orgId },
      },
    } = this.props;
    const {
      jobFilterId, // 职位过滤条件
      statusFilterValue, // 候选人状态过滤条件
      rpoRecommend,
      candidateNameFilterValue,
      showRejectedApplication,
      showDuplicatedApplication,
      showCommentModal,
      application, // 与hr沟通选中的application
      recommendStatus,
      recommendTag,

      page,
      recommendStatusCounts,
    } = this.state;

    const { headhunterApplicationStatus, stages, prohibitHeadhunterDownloadResume } =
      orgEntities[orgId] || {};

    /**
     * 构建候选人状态下拉框选项
     */
    let statusFilterOptions = [];
    // 1. 用于过滤候选人的下拉选择框的选项，模糊和真实的选项不同
    if (headhunterApplicationStatus === HEADHUNTER_APPLICATION_STATUS.REAL_STATUS) {
      // 真实状态
      statusFilterOptions = (stages || []).map((stage) => ({
        label: i18n._(stage.name),
        value: stage.id,
      }));
    } else {
      // 模糊状态
      statusFilterOptions.push(ADDITIONAL_STAGE_OPTIONS.PROCESS);
    }

    // 2. 是否添加淘汰状态
    if (showRejectedApplication) {
      statusFilterOptions.push(ADDITIONAL_STAGE_OPTIONS.REJECTED);
    }

    // 3. 添加已入职和删除状态
    statusFilterOptions.push(ADDITIONAL_STAGE_OPTIONS.HIRED, ADDITIONAL_STAGE_OPTIONS.DELETED);

    // 当前选中的application的职位负责人和协助人
    const managers = relatedJobEntities?.[application?.job?.id]?.managers ?? [];

    const tabsOptions = TAG_OPTIONS.map((tag) => ({
      ...tag,
      label: `${tag.label} ${recommendStatusCounts[tag.value]}`,
    }));

    return (
      <div className={classNames('orgdetail__content', cms.mgt24)}>
        <TagSelect
          className={classNames(cms.block, cms.mgt24, cms.mgb24)}
          options={tabsOptions}
          value={recommendTag}
          color="gray"
          onChange={this.onStatusChange}
        />
        <Spacing spacing="lg" className={styles.filterArea}>
          {recommendTag !== RECOMMEND_STATUS_TAG.SUCCESS && (
            <Filter
              customContentClassName={classNames(cms.mg0, cms.minWFull)}
              disableClear
              value={recommendStatus}
              options={
                recommendTag === RECOMMEND_STATUS_TAG.PENDING
                  ? STATUS_PENDING_OPTIONS
                  : STATUS_REFUSE_OPTIONS
              }
              customContent={() => {
                return (
                  recommendTag === RECOMMEND_STATUS_TAG.PENDING
                    ? STATUS_PENDING_OPTIONS
                    : STATUS_REFUSE_OPTIONS
                ).map((item) => {
                  return (
                    <div className={classNames(cms.flex, cms.alignCenter)}>
                      <Menu.PresetItem
                        key={item.value}
                        className={styles.menu}
                        selectedType="tick"
                        isSelected={recommendStatus === item.value}
                        title={<AutoEllipsis>{item.label}</AutoEllipsis>}
                        onClick={() => this.onRecommendStatusChange(item.value)}
                      />
                      {item.value === RECOMMEND_STATUS.CANDIDATE_UN_CONFIRM && (
                        <Dropdown
                          triggerType="hover"
                          showArrow
                          placement="right"
                          className={styles.dropdown}
                          wrapperClassName={cms.mgr16}
                          popup={
                            <div className={cms.mg8}>
                              {i18n.t`若企业启用「隐私保护」，您的每次推荐均需候选人本人确认`}
                            </div>
                          }
                        >
                          <Icon name="info" color="n50" />
                        </Dropdown>
                      )}
                    </div>
                  );
                });
              }}
              onChange={this.onRecommendStatusChange}
            />
          )}
          <Input
            size="md"
            clearable
            stretched={false}
            placeholder={i18n.t`请输入候选人姓名`}
            value={candidateNameFilterValue}
            suffix={<Icon name="search" />}
            onChange={this.onNameChange}
          />
          <JobFilter
            relatedJobEntities={relatedJobEntities}
            onJobFilterChange={this.onJobFilterChange}
            value={jobFilterId}
          />
          {recommendTag === RECOMMEND_STATUS.RECOMMEND_SUCCESS && (
            <Filter
              prefix={i18n.t`候选人状态`}
              value={statusFilterValue}
              options={statusFilterOptions}
              onChange={this.onStatusFilterChange}
            />
          )}
          <Filter
            prefix={i18n.t`猎头类型`}
            value={rpoRecommend}
            options={HEADHUNTER_TYPE_OPTIONS}
            onChange={this.onHeadhunterTypeFilterChange}
          />
          <Checkbox
            checked={showRejectedApplication}
            onChange={this.togglePassedOption}
          >{i18n.t`显示已淘汰候选人`}</Checkbox>
        </Spacing>

        <ApplicationsTable
          orgId={orgId}
          page={page}
          recommendStatus={recommendStatus}
          showRejectedApplication={showRejectedApplication}
          showDuplicatedApplication={showDuplicatedApplication}
          statusFilterValue={statusFilterValue}
          jobFilterId={jobFilterId}
          candidateNameFilterValue={candidateNameFilterValue}
          rpoRecommend={rpoRecommend}
          applyIdToComment={applyIdToComment}
          headhunterApplicationStatus={headhunterApplicationStatus}
          prohibitHeadhunterDownloadResume={prohibitHeadhunterDownloadResume}
          onPageChange={(pageNumber) => this.setState({ page: pageNumber })}
          onOpenCommentModal={this.onOpenConversationModal}
        />
        <Modal
          contentClassName={cms.pdb24}
          title={i18n.t`给HR留言沟通`}
          isOpen={showCommentModal}
          size="lg"
          withFooter={false}
          onClose={this.closeModal}
        >
          <CandidateComment
            application={application}
            comments={applyIdToComment[application?.id]?.comments ?? []}
            orgId={orgId}
            managers={managers}
          />
        </Modal>
      </div>
    );
  }
}

ApplicationList.propTypes = {
  jobFilterId: PropTypes.string,
  applyIdToComment: PropTypes.object,
};

ApplicationList.defaultProps = {
  jobColumnTitles: [],
};

const mapStateToProps = ({ me, org, jobs, applications, comments }) => ({
  me,
  orgEntities: org.entities,
  relatedJobEntities: jobs.relatedJobEntities,
  applicationEntities: applications.entities,
  applicationIdList: applications.idList,
  applicationNoMore: applications.applicationNoMore,
  minApplicationId: applications.minApplicationId,
  minAppliedAt: applications.minAppliedAt,
  applicationLoading: applications.loading,
  filter: applications.filter,
  applyIdToComment: comments.applyIdToComment,
});

const mapDispatchToProps = {
  createComment,
  getComment,
};

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationList);
